import type { ReactNode } from "react";
import { cn } from "@/lib/utils";

/**
 * Editorial "term — body" definition row for Hebrew RTL content.
 *
 * Two layouts:
 *   - "stacked" (default): bold term on its own line, body on the next.
 *     Best for narrow reading widths and long bodies.
 *   - "inline": term and body on a single line, separated by a thin
 *     middle dot (· ).
 *   - "two-column": term occupies a fixed-width column on the inline-end
 *     (right in RTL), body fills the rest. Best for short term + long body
 *     in a list of multiple definitions.
 *
 * Use a stack of these (e.g. inside a SectionCard) to build a structured
 * definition list.
 */
export type DefinitionRowProps = {
  /** The term, rendered bold. */
  term: ReactNode;
  /** The definition body. */
  body: ReactNode;
  /** Layout variant. Default: "stacked". */
  layout?: "stacked" | "inline" | "two-column";
  /** Extra classes merged onto the outer wrapper. */
  className?: string;
};

export function DefinitionRow({
  term,
  body,
  layout = "stacked",
  className,
}: DefinitionRowProps) {
  if (layout === "two-column") {
    return (
      <div
        className={cn(
          "grid grid-cols-[140px_1fr] gap-x-5 gap-y-1 py-2",
          "border-b border-stone-100 last:border-b-0",
          className,
        )}
      >
        <dt className="text-sm font-semibold text-stone-900 leading-[1.7] tracking-tight">
          {term}
        </dt>
        <dd className="text-[15px] text-stone-700 leading-[1.75]">{body}</dd>
      </div>
    );
  }

  if (layout === "inline") {
    return (
      <p
        className={cn(
          "text-[15px] leading-[1.8] text-stone-700",
          className,
        )}
      >
        <span className="font-semibold text-stone-900">{term}</span>
        <span className="mx-2 text-stone-400" aria-hidden="true">
          ·
        </span>
        <span>{body}</span>
      </p>
    );
  }

  // stacked
  return (
    <div className={cn("py-1.5", className)}>
      <p className="text-[15px] font-semibold text-stone-900 tracking-tight leading-[1.6]">
        {term}
      </p>
      <p className="mt-1 text-[15px] text-stone-700 leading-[1.8]">{body}</p>
    </div>
  );
}
